import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as ls from 'local-storage';
import { getUser } from 'apis/rest/User';
import { apiInterceptor } from 'utils/apiInterceptor';
import { GOOGLE_OAUTH_URL, googleOAuthErrorMessages } from './constant';

const LAST_VALID_PAGE_KEY = 'last_valid_page';

export default function useGoogleLogin() {
    const destination = window.localStorage.getItem(LAST_VALID_PAGE_KEY) || '';
    const history = useHistory();
    const searchParams = new URLSearchParams(window.location.search);

    const domain = window.location.origin || '';
    const isExhibitor =
        domain.includes(process.env.REACT_APP_EXHIBITOR_DASHBOARD_DOMAIN) ||
        domain === process.env.REACT_APP_EXHIBITOR_DASHBOARD_DOMAIN;

    const [googleError, setGoogleError] = useState(null);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    useEffect(() => {
        if (code && !state) {
            setIsGoogleLoading(true);
            exchangeCodeForToken();
        }
    }, [code, state, isExhibitor]);

    useEffect(() => {
        if (error) {
            setGoogleError(googleOAuthErrorMessages[error] || error);
        }
    }, [error]);

    async function exchangeCodeForToken() {
        try {
            const API_URL = isExhibitor
                ? `${process.env.REACT_APP_MYXP_API_V2}/sponsor/auth/login/google`
                : `${process.env.REACT_APP_MYXP_API}/manager/auth/login/google`;
            const response = await apiInterceptor(API_URL, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code }),
                ignoreResponseValidation: true,
            });
            const data = await response.json();
            if (data.status) {
                if (isExhibitor) {
                    ls.set('sponsorTempAuth', data.token);
                    ls.set('sponsorIdentifier', 'exhibitor');

                    await new Promise((resolve) => {
                        setTimeout(resolve, 200);
                    });
                    history.replace('/company-profiles');
                } else {
                    if (data['2FAToken']) {
                        ls.set('2FAToken', data['2FAToken']);
                        let emailFromToken = null;
                        try {
                            const tokenPayload = JSON.parse(
                                atob(data['2FAToken'].split('.')[1])
                            );
                            emailFromToken = tokenPayload?.email;
                        } catch (_) {}
                        return history.replace({
                            pathname: '/2fa',
                            payload: emailFromToken || 'sso',
                            identifier: data.identifier || null,
                        });
                    }

                    ls.set('auth', 'authenticated');

                    if (data.require2FASetup) {
                        ls.set('require2FASetup', true);
                        let emailFromToken = null;
                        try {
                            const accessToken =
                                data.token?.access_token || data.token;
                            if (typeof accessToken === 'string') {
                                const tokenPayload = JSON.parse(
                                    atob(accessToken.split('.')[1])
                                );
                                emailFromToken = tokenPayload?.email;
                            }
                        } catch (_) {}
                        return history.replace({
                            pathname: '/2fa-setup',
                            payload: emailFromToken || 'sso',
                            identifier: data.identifier || null,
                        });
                    }

                    if (destination && destination !== '/')
                        window.location.href = destination;

                    await new Promise((resolve) => {
                        setTimeout(resolve, 300);
                    });

                    const userResponse = await getUser();

                    if (userResponse?.user?.company?.id)
                        window.location.href = `/company/${userResponse?.user?.company?.id}/project`;
                    else window.location.href = '/';
                }
            } else {
                setGoogleError(
                    googleOAuthErrorMessages[data.message] || data.message
                );
            }
        } catch (error) {
            setGoogleError(
                googleOAuthErrorMessages[error.message] || error.message
            );
        } finally {
            setIsGoogleLoading(false);
        }
    }

    function handleLoginWithGoogle() {
        window.location.href = GOOGLE_OAUTH_URL;
    }
    return {
        handleLoginWithGoogle,
        googleError,
        isGoogleLoading,
    };
}
